/**
 * React Query Configuration Utilities
 *
 * This module provides standardized configuration options for React Query hooks
 * across the application, ensuring consistency in caching, retry logic, and
 * data fetching behavior.
 */

import { GraphQLResult } from '@aws-amplify/api-graphql';
import { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';

/**
 * AppSync GraphQL error structure.
 * Backend sends errorType for SaveDeck and Publish operations (e.g., "ValidationError")
 */
interface AppSyncGraphQLError {
  message: string;
  errorType?: string;
}

/**
 * Standard React Query options interface
 */
export interface StandardQueryOptions {
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
  cacheTime?: number; // Deprecated alias for gcTime (for backward compatibility)
  refetchInterval?: number;
  retry?: number | ((failureCount: number, error: Error) => boolean);
  retryDelay?: (attemptIndex: number) => number;
  retryBypassExceptions?: string[]; // Exception types to bypass during retry
  /**
   * RUM operation label for this query, surfaced as `meta.rumOp` and read by the global
   * api-latency instrumentation (see `utils/rum/rumQueryLatency.ts`). Set it to the GraphQL
   * operation name via `extractGraphqlOperationName(<queryDoc>)`.
   */
  rumOp?: string;
}

/**
 * Standard Mutation Query options interface
 */
export interface StandardMutationOptions<TData = unknown, TError = Error, TVariables = void> {
  retry?: number | ((failureCount: number, error: TError) => boolean);
  retryDelay?: (attemptIndex: number) => number;
  retryBypassExceptions?: string[]; // Exception types to bypass during retry
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: TError, variables: TVariables) => void;
  onSettled?: (data: TData | undefined, error: TError | null, variables: TVariables) => void;
  /**
   * RUM operation label for this mutation, surfaced as `meta.rumOp` (see StandardQueryOptions.rumOp).
   */
  rumOp?: string;
}

/**
 * Default configuration values used across all hooks
 */
export const DEFAULT_QUERY_CONFIG = {
  REFETCH_INTERVAL: 15 * 60 * 1000, // 15 minutes
  STALE_TIME: 15 * 60 * 1000, // 15 minutes
  GC_TIME: 15 * 60 * 1000, // 15 minutes (garbage collection time)
  RETRY_COUNT: 3,
  MAX_RETRY_DELAY: 30000, // 30 seconds
  BASE_RETRY_DELAY: 1000, // 1 second
} as const;

/**
 * Creates a retry function that bypasses specific exceptions
 * @param retryCount - Number of retry attempts
 * @param bypassExceptions - Array of exception types/messages to bypass
 * @returns Retry function for React Query
 */
export const createRetryWithBypass = <TError extends Error = Error>(
  retryCount: number,
  bypassExceptions: string[] = [],
): ((failureCount: number, error: TError) => boolean) => {
  return (failureCount: number, error: TError): boolean => {
    const parsedError = error as unknown as GraphQLResult;
    const firstErrorFound = parsedError?.errors?.[0] as AppSyncGraphQLError;
    const errorMessage = firstErrorFound?.message || error?.message;
    const { errorType } = firstErrorFound || {};

    // Check if error should bypass retry
    const shouldBypass = bypassExceptions.some(
      (exception) =>
        errorMessage?.includes(exception) || error?.name?.includes(exception) || errorType?.includes(exception),
    );

    if (shouldBypass) {
      console.debug('❌ Bypassing retry for:', errorMessage);
      return false; // Don't retry
    }

    const shouldRetry = failureCount < retryCount;
    console.debug('🔄 Should retry:', shouldRetry, `(${failureCount}/${retryCount})`);
    return shouldRetry;
  };
};

/**
 * Standard retry delay function with exponential backoff
 * @param attemptIndex - The current retry attempt (0-based)
 * @returns Delay in milliseconds
 */
export const standardRetryDelay = (attemptIndex: number): number => {
  return Math.min(DEFAULT_QUERY_CONFIG.BASE_RETRY_DELAY * 2 ** attemptIndex, DEFAULT_QUERY_CONFIG.MAX_RETRY_DELAY);
};

/**
 * Creates standardized React Query options for hooks
 * @param customOptions - Custom options to override defaults
 * @returns Standardized React Query options
 */
export const createStandardQueryOptions = <TData = unknown, TError extends Error = Error>(
  customOptions?: StandardQueryOptions,
): Partial<UseQueryOptions<TData, TError>> => {
  const retryCount = typeof customOptions?.retry === 'number' ? customOptions.retry : DEFAULT_QUERY_CONFIG.RETRY_COUNT;
  const retryFunction = customOptions?.retryBypassExceptions?.length
    ? createRetryWithBypass<TError>(retryCount, customOptions.retryBypassExceptions)
    : (customOptions?.retry ?? DEFAULT_QUERY_CONFIG.RETRY_COUNT);

  return {
    enabled: customOptions?.enabled ?? true,
    refetchInterval: customOptions?.refetchInterval ?? DEFAULT_QUERY_CONFIG.REFETCH_INTERVAL,
    staleTime: customOptions?.staleTime ?? DEFAULT_QUERY_CONFIG.STALE_TIME,
    gcTime: customOptions?.gcTime ?? customOptions?.cacheTime ?? DEFAULT_QUERY_CONFIG.GC_TIME,
    retry: retryFunction,
    retryDelay: customOptions?.retryDelay ?? standardRetryDelay,
    ...(customOptions?.rumOp ? { meta: { rumOp: customOptions.rumOp } } : {}),
  };
};

/**
 * Creates standardized React Query options for infinite query hooks.
 * Returns only config fields (enabled, retry, staleTime, etc.) without queryFn typing,
 * making it safe to spread into useInfiniteQuery without TPageParam conflicts.
 * @param customOptions - Custom options to override defaults
 * @returns Standardized options compatible with useInfiniteQuery
 */
export const createStandardInfiniteQueryOptions = (customOptions?: StandardQueryOptions) => {
  const retryCount = typeof customOptions?.retry === 'number' ? customOptions.retry : DEFAULT_QUERY_CONFIG.RETRY_COUNT;
  const retryFunction = customOptions?.retryBypassExceptions?.length
    ? createRetryWithBypass(retryCount, customOptions.retryBypassExceptions)
    : (customOptions?.retry ?? DEFAULT_QUERY_CONFIG.RETRY_COUNT);

  return {
    enabled: customOptions?.enabled ?? true,
    refetchInterval: customOptions?.refetchInterval ?? DEFAULT_QUERY_CONFIG.REFETCH_INTERVAL,
    staleTime: customOptions?.staleTime ?? DEFAULT_QUERY_CONFIG.STALE_TIME,
    gcTime: customOptions?.gcTime ?? customOptions?.cacheTime ?? DEFAULT_QUERY_CONFIG.GC_TIME,
    retry: retryFunction,
    retryDelay: customOptions?.retryDelay ?? standardRetryDelay,
    ...(customOptions?.rumOp ? { meta: { rumOp: customOptions.rumOp } } : {}),
  };
};

/**
 * Creates standardized React Query options for mutations
 * @param customOptions - Custom mutation options
 * @returns Standardized React Query mutation options
 */
export const createStandardMutationOptions = <TData = unknown, TError extends Error = Error, TVariables = void>(
  customOptions?: StandardMutationOptions<TData, TError, TVariables>,
): Partial<UseMutationOptions<TData, TError, TVariables>> => {
  const retryCount = typeof customOptions?.retry === 'number' ? customOptions.retry : DEFAULT_QUERY_CONFIG.RETRY_COUNT;
  const retryFunction = customOptions?.retryBypassExceptions?.length
    ? createRetryWithBypass<TError>(retryCount, customOptions.retryBypassExceptions)
    : (customOptions?.retry ?? DEFAULT_QUERY_CONFIG.RETRY_COUNT);

  return {
    retry: retryFunction,
    retryDelay: customOptions?.retryDelay ?? standardRetryDelay,
    onSuccess: customOptions?.onSuccess,
    onError: customOptions?.onError,
    onSettled: customOptions?.onSettled,
    ...(customOptions?.rumOp ? { meta: { rumOp: customOptions.rumOp } } : {}),
  };
};

/**
 * Type-safe query and mutation key factory for consistent cache key generation
 */
export const createQueryOrMutationKey = (
  ...parts: (string | number | boolean | null | undefined)[]
): readonly string[] => {
  return parts
    .filter((part): part is string | number | boolean => part !== null && part !== undefined)
    .map(String) as readonly string[];
};
